import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, Link, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useForgotPassword } from '../../hooks/use-forgot-password.hook';
import { ForgotPasswordRequestDTO } from '../../types/authentication.dto';
import { PUBLIC_PANELS } from '../../../shared/public/constants/panel.constants';
import { clientAccentOverrides } from './authentication-form.styles';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ForgotPasswordFormProps {
  onNavigate: (panel: string) => void;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    ...clientAccentOverrides(theme),
  },
  submit: {
    backgroundColor: theme.palette.client.primary.main,
    color: '#fff',
    minHeight: 48,
  },
  links: { display: 'flex', flexDirection: 'column', gap: theme.spacing(1), marginTop: theme.spacing(1) },
}));

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onNavigate }) => {
  const classes = useStyles();
  const [isSent, setIsSent] = useState(false);
  const { isSubmitting, error, submit } = useForgotPassword();

  const { control, handleSubmit, formState } = useForm<ForgotPasswordRequestDTO>({
    mode: 'onSubmit',
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const isSuccess = await submit(values);

    if (isSuccess) setIsSent(true);
  });

  if (isSent) {
    return (
      <Typography variant="body1">
        Si un compte existe pour cette adresse, un lien vient d&apos;être envoyé.
      </Typography>
    );
  }

  return (
    <form className={classes.form} onSubmit={onSubmit} noValidate>
      <Controller
        name="email"
        control={control}
        rules={{
          required: 'Veuillez saisir votre adresse mail',
          pattern: { value: EMAIL_PATTERN, message: "L'adresse mail n'est pas valide" },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            autoFocus
            label="Adresse mail"
            type="email"
            autoComplete="email"
            variant="outlined"
            error={Boolean(formState.errors.email)}
            helperText={formState.errors.email?.message}
            inputProps={{ name: 'email', 'aria-invalid': Boolean(formState.errors.email) }}
          />
        )}
      />

      <Box aria-live="polite">
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>

      <Button className={classes.submit} type="submit" variant="contained" disabled={isSubmitting}>
        Envoyer le lien
      </Button>

      <div className={classes.links}>
        <Link component="button" type="button" onClick={() => onNavigate(PUBLIC_PANELS.LOGIN)}>
          Retour à la connexion
        </Link>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
